import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { customEmailValidator } from '../../../shared/validators/email.validator';
import { LoadingService } from '../../../service/loading-service.service';
import { SnackbarService } from '../../../shared/snackbar/snackbar.service';
import { take, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import * as fromApp from '../../../store/app.reducer';
import * as AssignmentsActions from '../../../components/assignments/store/assignments.actions';
import * as AuthActions from '../../../auth/store/auth.actions';
import * as CompanyActions from '../../../components/company/store/company.actions';
import { CustomerModel } from '../../../models/customer.model';
import { PaymentService } from '../../../shared/services/payment.service';

@Component({
    selector: 'app-payment-modal',
    imports: [CommonModule, TranslateModule, ReactiveFormsModule],
    templateUrl: './payment-modal.component.html',
    styleUrl: './payment-modal.component.scss'
})
export class PaymentModalComponent implements OnInit, OnDestroy {
    private readonly AUTO_CREATED_CUSTOMER_FLAG_PREFIX = 'auto_created_customer_pending_checkout_';
    eInvoiceChecked: boolean = false;
    selectedPaymentMethod: string = 'invoice';
    selectedInvoiceTiming: string = 'next-month';
    showInvoiceDetails: boolean = true;
    invoiceForm: FormGroup;
    formSubmitted: boolean = false;
    isProcessingCardPayment: boolean = false;
    isCreatingAssignment: boolean = false;
    /** Job-place contact (from step3) - displayed for context */
    jobPlaceContactName: string = '';
    jobPlaceContactPhone: string = '';
    jobPlaceName: string = '';
    /** Organization number search (same API as create company) */
    searchResults: CustomerModel[] = [];
    selectedCompanyFromSearch: CustomerModel | null = null;
    orgSearchError: string | null = null;
    /** True while the organization search API is in progress (search button shows loading). */
    isSearchingOrganization: boolean = false;
    private companySub?: Subscription;
    private searchCompleteSub?: Subscription;
    private organizationNumberValueSub?: Subscription;

    constructor(
        public dialogRef: MatDialogRef<PaymentModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: FormBuilder,
        private translate: TranslateService,
        private loadingService: LoadingService,
        private snackbarService: SnackbarService,
        private store: Store<fromApp.AppState>,
        private actions$: Actions,
        private paymentService: PaymentService
    ) {
        this.invoiceForm = this.fb.group({
            organizationNumber: ['', [Validators.required, this.organizationNumberValidator.bind(this)]],
            contactPerson: ['', [Validators.required]],
            invoiceEmail: ['', [Validators.required, customEmailValidator()]]
        });
        this.patchInvoiceForm();
        this.store.dispatch(new CompanyActions.ClearSearchResults());
    }

    ngOnInit(): void {
        this.companySub = this.store.select(state => state.company).subscribe(companyState => {
            this.searchResults = companyState.searchResults ?? [];
            this.orgSearchError = companyState.searchError ?? null;
        });

        this.organizationNumberValueSub = this.invoiceForm.get('organizationNumber')?.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged((prev, curr) => (prev?.toString?.().trim?.() ?? '') === (curr?.toString?.().trim?.() ?? ''))
            )
            .subscribe(() => {
                const rawValue = this.invoiceForm.get('organizationNumber')?.value?.toString().trim() ?? '';
                const normalized = this.normalizeOrganizationNumber(rawValue);
                if (normalized.length > 9) {
                    this.searchOrganization();
                } else if (rawValue === '') {
                    this.store.dispatch(new CompanyActions.ClearSearchResults());
                }
            });
    }

    ngOnDestroy(): void {
        this.companySub?.unsubscribe();
        this.searchCompleteSub?.unsubscribe();
        this.organizationNumberValueSub?.unsubscribe();
        this.store.dispatch(new CompanyActions.ClearSearchResults());
    }

    /** Search organization by number (same API as create new company). Always allows verification via API when user clicks search, even when field is pre-filled or format is invalid. */
    searchOrganization(): void {
        const raw = this.invoiceForm.get('organizationNumber')?.value?.toString().trim() ?? '';
        const query = this.normalizeOrganizationNumber(raw);
        if (query.length > 6) {
            this.searchCompleteSub?.unsubscribe();
            this.isSearchingOrganization = true;
            this.store.dispatch(new CompanyActions.SearchCompany({ query }));
            this.searchCompleteSub = this.actions$.pipe(
                ofType(CompanyActions.SEARCH_COMPANY_SUCCESS, CompanyActions.SEARCH_COMPANY_FAILED),
                take(1)
            ).subscribe(() => {
                this.isSearchingOrganization = false;
            });
        } else {
            this.store.dispatch(new CompanyActions.ClearSearchResults());
        }
    }

    /** Apply selected company from search: update form, call API to update customer organisation number, then clear results. */
    selectCompanyFromSearch(company: CustomerModel): void {
        const orgNumber = company.idNumber ?? '';
        this.selectedCompanyFromSearch = company;
        this.invoiceForm.patchValue({
            organizationNumber: orgNumber,
            contactPerson: this.invoiceForm.get('contactPerson')?.value || company.companyName || ''
        });
        this.invoiceForm.get('organizationNumber')?.updateValueAndValidity();
        this.store.dispatch(new CompanyActions.ClearSearchResults());
        const customerId = this.data?.customerId;
        if (customerId && orgNumber) {
            this.store.dispatch(new AuthActions.UpdateCustomerOrganisationNumber({
                customerId,
                organisationNumber: this.normalizeOrganizationNumber(orgNumber),
                companyName: company?.companyName || '',
                contactPerson: this.invoiceForm.get('contactPerson')?.value || company?.contactPerson || company?.companyName || '',
                email: this.invoiceForm.get('invoiceEmail')?.value || company?.email || '',
                phone: company?.phone || '',
                address: company?.address || '',
                city: company?.city || '',
                country: company?.country || '',
                biography: company?.biography || '',
                customerWebsite: company?.customerWebsite || ''
            }));
        }
    }

    private patchInvoiceForm(): void {
        const customer = this.data?.customer;
        const employee = this.data?.employee;
        const billingInfo = customer?.billingInformation;
        const assignmentData = this.data?.assignmentData;
        const latestInvoice = this.data?.latestInvoice;

        // Job-place specific contact (from step3 contact form)
        this.jobPlaceContactName = assignmentData?.contactPersonName || '';
        this.jobPlaceContactPhone = assignmentData?.contactPersonPhone || '';
        this.jobPlaceName = this.data?.jobPlaceName || this.data?.jobPlaceAddress || '';

        // Organisation number: selected customer's idNumber only; leave blank so the user can enter one when missing.
        const organizationNumber = (customer?.idNumber ?? '').toString().trim();
        const contactPerson = latestInvoice?.contactPerson
            || latestInvoice?.contact_person
            || this.jobPlaceContactName
            || billingInfo?.contactName
            || customer?.contactPerson
            || '';
        const invoiceEmail = latestInvoice?.email
            || latestInvoice?.invoiceEmail
            || billingInfo?.email
            || customer?.email
            || employee?.email
            || '';
        this.invoiceForm.patchValue({
            organizationNumber: organizationNumber,
            contactPerson: contactPerson,
            invoiceEmail: invoiceEmail
        });
        const eInvoice = latestInvoice?.eInvoice ?? latestInvoice?.e_invoice;
        if (eInvoice != null) {
            this.eInvoiceChecked = !!eInvoice;
        }

        // Edit case or Save & Continue with card: pre-select payment method, billing reference, invoice timing from the 3 fields
        const editValues = this.data?.editBillingValues;
        const hasEditValues = this.data?.isEditBillingCase || this.data?.openAndGoToStripe;
        if (hasEditValues && editValues) {
            if (editValues.paymentMethod != null && editValues.paymentMethod !== '') {
                this.selectedPaymentMethod = editValues.paymentMethod;
                this.showInvoiceDetails = editValues.paymentMethod === 'invoice';
                // Do not auto-redirect when opened from "Redigera faktureringsuppgifter" (isEditBillingCase only)
            }
            if (editValues.invoiceTime != null && editValues.invoiceTime !== '') {
                this.selectedInvoiceTiming = editValues.invoiceTime === 'immediate' ? 'immediate' : 'next-month';
            }
            if (editValues.billingReference != null && editValues.billingReference !== '') {
                this.invoiceForm.patchValue({ contactPerson: editValues.billingReference });
            }
        }
        // When user set reference on step3 (Faktureringsuppgifter), show same value in checkout modal
        if (editValues?.billingReference != null && editValues.billingReference !== '') {
            this.invoiceForm.patchValue({ contactPerson: editValues.billingReference });
        }
        // When opened from Save & Continue with card pre-selected, go to Stripe after view is ready
        if (this.data?.openAndGoToStripe && this.selectedPaymentMethod === 'card') {
            setTimeout(() => this.processCardPayment(), 150);
        }

        // Show validation for prefilled organization number on open (e.g. invalid format from API)
        if (this.showInvoiceDetails) {
            const orgControl = this.invoiceForm.get('organizationNumber');
            if (orgControl?.value && orgControl.invalid) {
                orgControl.markAsTouched();
            }
        }

        // Auto-run search when organization number is pre-filled so API verification happens without user clicking search
        if (this.showInvoiceDetails && organizationNumber && organizationNumber.length > 6) {
            setTimeout(() => this.searchOrganization(), 200);
        }
    }

    /** Valid formats: 10 digits (559167-4089, 5591674089) or 12 digits (19630409-4078, 196304094078). */
    private static readonly ORG_NUMBER_REGEX = /^([0-9]{6}|[0-9]{8})-?[0-9]{4}$/;

    organizationNumberValidator(control: AbstractControl): { [key: string]: any } | null {
        const value = control.value?.toString().trim();
        if (!value) {
            return null;
        }
        const valid = PaymentModalComponent.ORG_NUMBER_REGEX.test(value);
        return valid ? null : { invalidOrgNumber: true };
    }

    /** True only when organisation number is non-empty and valid format. Used to disable submit until org is valid. */
    get isOrganisationNumberValidForSubmit(): boolean {
        const v = this.invoiceForm.get('organizationNumber')?.value?.toString().trim?.();
        if (!v) return false;
        return PaymentModalComponent.ORG_NUMBER_REGEX.test(v);
    }

    /** Returns organization number with dashes removed for API/DB (e.g. 559167-4089 -> 5591674089). */
    private normalizeOrganizationNumber(value: string): string {
        return value?.toString().trim().replace(/-/g, '') ?? '';
    }

    private hasAutoCreatedRegistrationFlag(customer: CustomerModel | null | undefined): boolean {
        const flag = (customer as any)?.isAutoCreatedFromRegistration;
        return flag === true || flag === 1 || flag === '1' || flag === 'true';
    }

    /**
     * Checkout sync rule:
     * - Use organization number API in all cases.
     * - When customer is auto-created on first registration, pass the flag for backend handling.
     */
    private syncCustomerBeforeCheckout(): void {
        const customerId = this.data?.customerId;
        if (!customerId) return;

        const rawOrgNumber = this.invoiceForm.get('organizationNumber')?.value?.toString().trim() ?? '';
        if (!PaymentModalComponent.ORG_NUMBER_REGEX.test(rawOrgNumber)) return;

        const normalizedOrgNumber = this.normalizeOrganizationNumber(rawOrgNumber);
        const customer: CustomerModel | undefined = this.data?.customer;
        const latestCustomerData: Partial<CustomerModel> = this.selectedCompanyFromSearch || customer || {};
        const localPendingFlag = localStorage.getItem(`${this.AUTO_CREATED_CUSTOMER_FLAG_PREFIX}${customerId}`) === 'true';
        const backendPendingFlag = this.hasAutoCreatedRegistrationFlag(customer);
        const shouldSendFlag = backendPendingFlag || localPendingFlag;
        const customerDetailsPayload = {
            companyName: latestCustomerData?.companyName || this.invoiceForm.value.contactPerson || '',
            contactPerson: this.invoiceForm.value.contactPerson || latestCustomerData?.contactPerson || '',
            email: this.invoiceForm.value.invoiceEmail || latestCustomerData?.email || '',
            phone: latestCustomerData?.phone || this.data?.assignmentData?.contactPersonPhone || '',
            address: latestCustomerData?.address || '',
            city: latestCustomerData?.city || '',
            country: latestCustomerData?.country || '',
            biography: latestCustomerData?.biography || '',
            customerWebsite: latestCustomerData?.customerWebsite || ''
        };

        this.store.dispatch(new AuthActions.UpdateCustomerOrganisationNumber({
            customerId,
            organisationNumber: normalizedOrgNumber,
            isAutoCreatedFromRegistration: shouldSendFlag,
            ...customerDetailsPayload
        }));

        if (shouldSendFlag) {
            localStorage.removeItem(`${this.AUTO_CREATED_CUSTOMER_FLAG_PREFIX}${customerId}`);
        }
    }

    onEInvoiceChange(event: any): void {
        this.eInvoiceChecked = event.target.checked;
    }

    onPaymentMethodChange(method: string): void {
        this.selectedPaymentMethod = method;
        if (method === 'invoice') {
            this.showInvoiceDetails = true;
            // Re-enable validators when invoice is selected
            this.invoiceForm.get('organizationNumber')?.setValidators([Validators.required, this.organizationNumberValidator.bind(this)]);
            this.invoiceForm.get('contactPerson')?.setValidators([Validators.required]);
            this.invoiceForm.get('invoiceEmail')?.setValidators([Validators.required, customEmailValidator()]);
        } else {
            this.showInvoiceDetails = false;
            // Clear validators when card is selected
            this.invoiceForm.get('organizationNumber')?.clearValidators();
            this.invoiceForm.get('contactPerson')?.clearValidators();
            this.invoiceForm.get('invoiceEmail')?.clearValidators();
            // Automatically process card payment

            this.processCardPayment();
        }
        // Update validity after changing validators
        this.invoiceForm.get('organizationNumber')?.updateValueAndValidity();
        this.invoiceForm.get('contactPerson')?.updateValueAndValidity();
        this.invoiceForm.get('invoiceEmail')?.updateValueAndValidity();
    }

    onInvoiceTimingChange(timing: string): void {
        this.selectedInvoiceTiming = timing;
    }

    getErrorMessage(controlName: string): string {
        const control = this.invoiceForm.get(controlName);
        if (!control || !control.errors || (!control.touched && !this.formSubmitted)) {
            return '';
        }
        const errors = control.errors;
        // Convert camelCase to kebab-case for translation keys
        const kebabCaseName = controlName.replace(/([A-Z])/g, '-$1').toLowerCase();

        if (errors['required']) {
            const translationKey = `payment-modal.validation.${kebabCaseName}-required`;
            const message = this.translate.instant(translationKey);
            return message !== translationKey ? message : '';
        }
        if (errors['email'] || errors['invalidOrgNumber']) {
            const translationKey = `payment-modal.validation.${kebabCaseName}-invalid`;
            const message = this.translate.instant(translationKey);
            return message !== translationKey ? message : '';
        }

        return '';
    }

    shouldShowError(controlName: string): boolean {
        const control = this.invoiceForm.get(controlName);
        return !!(control && control.invalid && (control.touched || this.formSubmitted) && this.selectedPaymentMethod === 'invoice');
    }

    /**
     * Builds normalized assignment payload with required fields (dates, timezone, etc.) for both card and invoice.
     * Uses this.data.dates as fallback when assignmentData.dates is missing (e.g. edit billing with card→invoice).
     */
    private getAssignmentPayload(contactPersonFallback?: string): { data: any; error?: string } {
        const assignmentCreateData = this.data?.assignmentData;
        if (!assignmentCreateData) {
            return { data: null, error: this.translate.instant('assignment-data-missing') };
        }
        const dates = Array.isArray(assignmentCreateData.dates) && assignmentCreateData.dates.length
            ? assignmentCreateData.dates
            : (Array.isArray(this.data?.dates) && this.data.dates.length ? this.data.dates : []);
        if (!dates.length) {
            return { data: null, error: this.translate.instant('guard-shift-details.common.dates-required') || 'Assignment dates are required. Please close this dialog and select dates on the previous step.' };
        }
        const contactPerson = contactPersonFallback ?? assignmentCreateData?.contactPersonName ?? '';
        const data = {
            ...assignmentCreateData,
            contactPersonName: assignmentCreateData?.contactPersonName ?? contactPerson,
            contactPersonPhone: assignmentCreateData?.contactPersonPhone ?? '',
            timezone: assignmentCreateData?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            engagementType: assignmentCreateData?.engagementType ?? 2,
            numberOfPersonnel: assignmentCreateData?.numberOfPersonnel ?? assignmentCreateData?.guardsNumber ?? 1,
            dates,
            assignmentCategoryId: assignmentCreateData?.assignmentCategoryId,
            numberOfLeaders: assignmentCreateData?.numberOfLeaders ?? 0,
            leadersIds: assignmentCreateData?.leadersIds ?? [],
            guardsIds: assignmentCreateData?.guardsIds ?? []
        };
        return { data };
    }

    //For card payment
    processCardPayment(): void {
        if (this.isProcessingCardPayment) return;
        this.syncCustomerBeforeCheckout();
        const customerId = this.data?.customerId;
        if (!customerId) {
            console.error('Customer ID is missing');
            this.snackbarService.showError(this.translate.instant('customer-id-missing'));
            return;
        }
        const contactPerson = this.invoiceForm?.value?.contactPerson ?? this.data?.assignmentData?.contactPersonName ?? '';
        const { data, error } = this.getAssignmentPayload(contactPerson);
        if (error) {
            this.snackbarService.showError(error);
            return;
        }

        this.isProcessingCardPayment = true;
        this.loadingService.showLoader();
        this.store.dispatch(new AssignmentsActions.AddAssignment({
            customerId: customerId,
            data,
        }));

        // Step 2: Wait for assignment creation success or failure
        this.actions$.pipe(
            ofType(AssignmentsActions.ADD_ASSIGNMENT_SUCCESS, AssignmentsActions.ADD_ASSIGNMENT_FAILED),
            take(1)
        ).subscribe((action: any) => {
            if (action.type === AssignmentsActions.ADD_ASSIGNMENT_FAILED) {
                console.error('Assignment creation failed');
                this.isProcessingCardPayment = false;
                this.snackbarService.showError(this.translate.instant('failed-create-assignment-short'));
                this.loadingService.hideLoader();
                return;
            }
            const assignment = (action as AssignmentsActions.AddAssignmentSuccess).payload.assignment;
            const assignmentId = assignment?.assignmentId || assignment?.id;
            const customerId = this.data?.customerId;
            const jobPlaceId = this.data?.jobPlaceId;

            // Step 3: Store the 3 billing fields in job place table before redirecting to Stripe
            if (customerId && jobPlaceId) {
                this.store.dispatch(new AuthActions.StoreJobPlaceBillingInfo({
                    customerId,
                    jobPlaceId,
                    payment_method: 'card',
                    billing_reference: this.invoiceForm?.value?.contactPerson ?? '',
                    invoice_time: this.selectedInvoiceTiming ?? 'next-month',
                    invoice_email: this.invoiceForm?.value?.invoiceEmail ?? ''
                }));
                this.actions$.pipe(
                    ofType(AuthActions.STORE_JOB_PLACE_BILLING_INFO_SUCCESS, AuthActions.STORE_JOB_PLACE_BILLING_INFO_FAILED),
                    take(1)
                ).subscribe(() => {
                    this.createCheckoutAndRedirect(assignmentId);
                });
            } else {
                this.createCheckoutAndRedirect(assignmentId);
            }
        });
    }

    private createCheckoutAndRedirect(assignmentId: number): void {
        if (!assignmentId) {
            this.isProcessingCardPayment = false;
            this.loadingService.hideLoader();
            this.snackbarService.showError(this.translate.instant('assignment-id-missing'));
            return;
        }
        const payload = { assignment_id: assignmentId };
        this.paymentService.createCheckoutSession(payload).subscribe({
            next: (response: any) => {
                const checkoutUrl = response.checkout_url;
                const checkoutSessionId = response.checkout_session_id;

                // Store session ID for tracking/verification if needed
                if (checkoutSessionId) {
                    console.log('Checkout session ID:', checkoutSessionId);
                    // Optionally store in localStorage or service for later use
                    localStorage.setItem('stripe_session_id', checkoutSessionId);
                }

                if (checkoutUrl) {
                    // Open Stripe checkout in a new tab - current tab stays open
                    // Use window.open with _blank to open in new tab
                    const newWindow = window.open(checkoutUrl, '_blank');

                    // Check if popup was blocked
                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                        console.warn('Popup blocked by browser');
                        this.isProcessingCardPayment = false;
                        this.loadingService.hideLoader();
                        this.snackbarService.showError(this.translate.instant('popups-blocked-for-checkout'));

                        // Don't redirect current tab - keep it open
                        // User can manually copy the URL or allow popups
                        console.log('Checkout URL (popup blocked):', checkoutUrl);
                    } else {
                        // Successfully opened in new tab
                        this.isProcessingCardPayment = false;
                        this.loadingService.hideLoader();
                        setTimeout(() => {
                            this.dialogRef.close({
                                paymentSuccess: false,
                                redirecting: true,
                                checkoutOpened: true
                            });
                        }, 300);
                    }
                } else {
                    console.error('No checkout URL received');
                    this.isProcessingCardPayment = false;
                    this.snackbarService.showError(this.translate.instant('checkout-url-missing'));
                    this.loadingService.hideLoader();
                }
            },
            error: (error: any) => {
                console.error('Failed to create checkout session:', error);
                this.isProcessingCardPayment = false;
                this.snackbarService.showError(this.translate.instant('checkout-session-failed'));
                this.loadingService.hideLoader();
            }
        });
    }

    onSubmit(): void {
        if (!this.isOrganisationNumberValidForSubmit) {
            return;
        }
        if (this.selectedPaymentMethod === 'card') {
            this.processCardPayment();
            return;
        }
        if (this.selectedPaymentMethod === 'invoice') {
            this.syncCustomerBeforeCheckout();
            this.formSubmitted = true;
            if (this.invoiceForm.invalid) {
                this.invoiceForm.markAllAsTouched();
                return;
            }

            // Prevent multiple submissions
            if (this.isCreatingAssignment) {
                console.warn('Assignment creation already in progress');
                return;
            }

            const customerId = this.data?.customerId;
            if (!customerId) {
                console.error('Customer ID is missing');
                this.snackbarService.showError(this.translate.instant('customer-id-missing'));
                return;
            }

            const { data: assignmentPayload, error } = this.getAssignmentPayload();
            if (error) {
                this.snackbarService.showError(error);
                return;
            }

            // Persist customer billing info when user has passed a valid company number (so it is stored even if they came from step3 without one)
            const customer = this.data?.customer;
            const billingInfo = customer?.billingInformation;
            const companyNumber = this.normalizeOrganizationNumber(this.invoiceForm.value.organizationNumber);
            if (customerId && companyNumber) {
                this.store.dispatch(new AuthActions.CreateBillingInfo({
                    customerId,
                    companyNumber,
                    contactName: this.invoiceForm.value.contactPerson ?? '',
                    email: this.invoiceForm.value.invoiceEmail ?? '',
                    phone: this.data?.assignmentData?.contactPersonPhone || billingInfo?.phone || (customer as any)?.phone || '',
                    companyName: billingInfo?.companyName || (customer as any)?.companyName || '',
                    address: billingInfo?.address || (customer as any)?.address || '',
                    addressSecond: billingInfo?.addressSecond,
                    postalCode: billingInfo?.postalCode,
                    city: billingInfo?.city || (customer as any)?.city || ''
                }));
            }

            // Set flag to prevent multiple calls
            this.isCreatingAssignment = true;

            // Step 1: Create assignment first - ALWAYS call this first (normalized payload so dates/required fields work in all cases)
            this.loadingService.showLoader();
            this.store.dispatch(new AssignmentsActions.AddAssignment({
                customerId: customerId,
                data: assignmentPayload,
            }));

            // Step 2: Wait for assignment creation success or failure - MUST complete before invoice API
            this.actions$.pipe(
                ofType(AssignmentsActions.ADD_ASSIGNMENT_SUCCESS, AssignmentsActions.ADD_ASSIGNMENT_FAILED),
                take(1)
            ).subscribe((action: any) => {
                // Reset flag
                this.isCreatingAssignment = false;

                if (action.type === AssignmentsActions.ADD_ASSIGNMENT_FAILED) {
                    console.error('Assignment creation failed');
                    this.snackbarService.showError(this.translate.instant('failed-create-assignment-short'));
                    this.loadingService.hideLoader();
                    return;
                }

                const assignment = (action as AssignmentsActions.AddAssignmentSuccess).payload.assignment;

                // Get assignmentId from the assignment object
                const assignmentId = assignment?.assignmentId || assignment?.id;
                if (!assignmentId) {
                    console.error('Assignment ID is missing from response!', assignment);
                    this.snackbarService.showError(this.translate.instant('assignment-id-missing'));
                    this.loadingService.hideLoader();
                    return;
                }

                // Step 3: Prepare invoice payload - ONLY after assignment is created
                // Map invoice timing to billing status values
                const billingStatus = this.selectedInvoiceTiming === 'immediate'
                    ? 'IMMEDIATELY_AFTER_SESSION'
                    : 'BEGINNING_OF_NEXT_MONTH';

                // Get estimated price from calculate price API response
                const estimatedPrice = this.data?.prices?.estimatedPrice || 0;

                const invoicePayload = {
                    orgNumber: this.normalizeOrganizationNumber(this.invoiceForm.value.organizationNumber),
                    assignmentId: assignmentId,
                    contactPerson: this.invoiceForm.value.contactPerson,
                    email: this.invoiceForm.value.invoiceEmail,
                    billingStatus: billingStatus,
                    eInvoice: this.eInvoiceChecked ? 1 : 0,
                    amount: estimatedPrice,
                    currency: 'sek',
                };

                // Store payment_method, billing_reference, invoice_time, invoice_email for job place on every invoice flow
                const customerId = this.data?.customerId;
                const jobPlaceId = this.data?.jobPlaceId;
                if (customerId && jobPlaceId) {
                    this.store.dispatch(new AuthActions.StoreJobPlaceBillingInfo({
                        customerId,
                        jobPlaceId,
                        payment_method: this.selectedPaymentMethod,
                        billing_reference: this.invoiceForm.value.contactPerson,
                        invoice_time: this.selectedInvoiceTiming,
                        invoice_email: this.invoiceForm.value.invoiceEmail
                    }));
                }

                // Step 4: Call invoice API using payment service - ONLY after assignment is created
                this.paymentService.submitAssignmentInvoice(invoicePayload as any)
                    .subscribe({
                        next: (response: any) => {
                            // Close modal with success flag after invoice API success
                            this.dialogRef.close({ paymentSuccess: true });
                        },
                        error: (error: any) => {
                            console.error('Invoice API call failed:', error);
                            this.loadingService.hideLoader();
                        }
                    });
            });
        }
    }
}
